import React, { useMemo } from 'react';
import { Button, CardActions, CircularProgress, Divider, Stack, Typography, useTheme } from '@mui/material';
import { Form, Formik } from 'formik';
import { useDispatch } from 'react-redux';
import { showToast } from '@/features/toast/toastSlice';
import { FormikErrorScroll } from '@/hooks/useFormikHelper';
import { useUpdateRelationshipCompanyMutation } from '@/features/relationshipCompanies/relationshipCompaniesApi';
import { useObjectContext } from '@/hooks/useObjectContext';
import { difference } from '@/utils/constants';
import {
	companyProfileProperties,
	toRelationshipCompanyInitialValues,
} from '@/components/forms/CreateRelationshipCompanyForm/fields';
import CompanyDetailsFields from '@/components/forms/CreateRelationshipCompanyForm/Fields/CompanyDetailsFields';
import { relationshipCompanyDetailsValidationSchema } from '@/components/forms/CreateRelationshipCompanyForm/validationSchema';

const RelationshipCompanyDetailsTab = () => {
	const theme = useTheme();
	const dispatch = useDispatch();
	const { relationshipCompany } = useObjectContext();
	const [updateRelationshipCompany, updateState] = useUpdateRelationshipCompanyMutation();

	const initialValues = useMemo(
		() => toRelationshipCompanyInitialValues(relationshipCompany),
		[relationshipCompany],
	);

	return (
		<Formik
			initialValues={initialValues}
			validationSchema={relationshipCompanyDetailsValidationSchema}
			enableReinitialize
			validateOnBlur
			onSubmit={async (values, { setSubmitting }) => {
				setSubmitting(true);
				try {
					const properties = difference(
						companyProfileProperties(initialValues),
						companyProfileProperties(values),
					);
					if (Object.keys(properties).length === 0) {
						return;
					}
					await updateRelationshipCompany({
						relationshipCompanyId: relationshipCompany?.id,
						properties,
					}).unwrap();
				} catch (error) {
					dispatch(
						showToast({
							message:
								error?.data?.message ??
								error?.message ??
								'Unable to update company.',
							error: true,
						}),
					);
				} finally {
					setSubmitting(false);
				}
			}}
		>
			{({ isSubmitting, dirty }) => {
				const disabled = isSubmitting || updateState.isLoading;
				return (
					<Form>
						<FormikErrorScroll isSubmitting={isSubmitting} />
						<Stack spacing={2} width='100%' direction='column'>
							<Typography variant='form_label' fontSize='18px'>
								Company details
							</Typography>
							<Divider />
							<CompanyDetailsFields disabled={disabled} />
						</Stack>
						<CardActions
							sx={{
								position: 'sticky',
								bottom: 0,
								zIndex: 1,
								backgroundColor: theme.palette.background.paper,
								borderTop: `1px solid ${theme.palette.divider}`,
								justifyContent: 'center',
							}}
						>
							<Button
								color='primary'
								type='submit'
								variant='contained'
								disabled={disabled || !dirty}
							>
								{isSubmitting ? (
									<CircularProgress
										size='2em'
										sx={{ color: theme.palette.primary.contrastText }}
									/>
								) : (
									'Save'
								)}
							</Button>
						</CardActions>
					</Form>
				);
			}}
		</Formik>
	);
};

export default RelationshipCompanyDetailsTab;
